import { ChatDto, ChatResponseDto } from '@/lib/interfaces/messages';
import { JobFileResponseDto } from '@/lib/interfaces/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { ChevronLeft, ChevronRight, Download, FileType, LoaderCircle, MoreVertical, Paperclip, Trash2, X } from 'lucide-react';
import { Dispatch, forwardRef, MutableRefObject, SetStateAction, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { io } from 'socket.io-client';
import { z } from 'zod';
import { useUser } from '../../hooks/use-user';
import useApiRequest from '../../lib/hooks/useRequest';
import { downloadURI, formatDateTime } from '../../lib/hooks/utils';
import { UserRole, UserType } from '../../lib/interfaces/user';
import { Button } from '../../ui/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormMessage } from '../../ui/components/ui/form';
import { Input } from '../../ui/components/ui/input';
import Preloader from '../../ui/components/ui/preloader';
import { Textarea } from '../../ui/components/ui/textarea';
import { Context } from '../root/context';
import ConfirmationDialog from '../../ui/components/ui/confirmation-dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../ui/components/ui/dropdown-menu';
import GoogleRatingModal from './customer/[id]/google-rating-modal';
import { RatingInfoResponseDto } from '@/lib/interfaces/customers/jobs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/components/ui/dialog';
import { Skeleton } from '../../ui/components/ui/skeleton';

const Chat = ({ id, type, setReload }: { id: string; type: 'internal' | 'customer'; setReload: Dispatch<SetStateAction<boolean>> }) => {
  const { apiRequest, apiFileRequest } = useApiRequest();
  const { user } = useUser();
  const userType = user?.userType === UserType.Customer ? 'customer-user' : 'internal-user';
  const [messages, setMessages] = useState<ChatDto[]>([]);
  const [chatToken, setChatToken] = useState<string | null>(null);
  const chat = useRef<HTMLDivElement>(null);
  const [files, setFiles] = useState<File[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [initLoading, setInitLoading] = useState(true);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [page, setPage] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const { isMobile } = useContext(Context);
  const [googleRatingModalOpen, setGoogleRatingModalOpen] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const observer = useRef<IntersectionObserver | null>(null);

  const formSchema = z
    .object({
      message: z.string().optional()
    })
    .superRefine((data, ctx) => {
      const message = data.message?.trim();

      if (!files && (!message || message === '')) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['message'],
          message: 'Beschreibung ist erforderlich'
        });
      }
    });

  type FormSchema = z.infer<typeof formSchema>;

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      message: ''
    }
  });

  const onSubmit = async (data: FormSchema) => {
    setSubmitting(true);
    if (files) {
      await apiFileRequest(`jobs/${type}/${id}/messages`, 'POST', files, {
        formdataName: 'files',
        customFields: {
          message: data.message ?? ''
        }
      });
      setFiles(null);
    } else {
      await apiFileRequest(`jobs/${type}/${id}/messages`, 'POST', undefined, {
        formdataName: 'files',
        customFields: {
          message: data.message ?? ''
        }
      });
    }
    setSubmitting(false);
    form.reset();
    getMessages();
  };

  const getMessages = useCallback(async () => {
    setIsLoading(true);
    const res = await apiRequest<ChatResponseDto>(`jobs/${type}/${id}/messages${page ? `?page=${page}` : ''}`, 'GET');
    if (res.data) {
      const newPosts = res.data.chat.docs;

      setMessages((oldValues) => {
        const combined = [...oldValues, ...newPosts];
        const final: ChatDto[] = [];
        combined.map((value) => {
          const index = final.findIndex((item) => item._id === value._id);
          if (index === -1) {
            final.push(value);
          }
          return null;
        });
        return final.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
      });

      setChatToken(res.data.chatKey);
      setHasNextPage(res.data.chat.hasNextPage);
      setInitLoading(false);
    }
    setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiRequest, id, page, type]);

  useEffect(() => {
    getMessages();
  }, [getMessages, initLoading]);

  const scrollToBottom = () => {
    if (chat.current) {
      chat.current.scrollTop = chat.current.scrollHeight;
    }
  };

  const patchNotification = useCallback(async () => {
    await apiRequest(`jobs/${type}/${id}/notifications`, 'PATCH');
  }, [apiRequest, id, type]);

  useEffect(() => {
    const implemented = true;

    if (chatToken && implemented) {
      const socketConnection = io(process.env.REACT_APP_API, {
        query: { jobId: id },
        extraHeaders: {
          authentication: chatToken
        }
      });

      socketConnection.on('newMessage', (message: ChatDto) => {
        setMessages((prevMessages) => [...prevMessages, { ...message, isOwn: message.belongsToUserProfilePictureUrl === user?.profilePictureUrl }]);
        patchNotification();
      });

      return () => {
        socketConnection.disconnect();
      };
    }
  }, [chatToken, id, patchNotification, user?.profilePictureUrl]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const messageIsOwn = (message: ChatDto) => {
    return message.belongsToUserType === userType;
  };

  const lastPostElementRef = useCallback(
    (node: HTMLElement | null) => {
      if (isLoading) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          if (hasNextPage) {
            setPage((prevPage) => prevPage + 1);
          }
        }
      });

      if (node) observer.current.observe(node);
    },
    [hasNextPage, isLoading]
  );

  return (
    <div className={`flex flex-col ${isMobile ? 'w-full max-h-[70vh]' : 'w-1/2'} rounded-[14px]`}>
      <GoogleRatingModal
        open={googleRatingModalOpen}
        onClose={() => {
          setGoogleRatingModalOpen(false);
          setReload((prev) => !prev);
        }}
      />

      <div className="px-6 py-4 bg-[#12282A] rounded-t-[14px] relative">
        <p className="text-center text-white text-2xl font-bold">Chat</p>
        {user?.userType === UserType.Internal && (user.userRole === UserRole.Admin || user.userRole === UserRole.SuperAdmin) && (
          <Button
            variant="ghost"
            size="sm"
            className="text-white hover:bg-[#ffffff] transition-colors duration-300 ease-in-out absolute top-1/2 -translate-y-1/2 right-4"
            onClick={async () => {
              const res = await apiRequest<{ fileUrl: string }>(`jobs/internal/${id}/messages/export-csv`, 'GET', {
                toast: {
                  toastText: 'Chat wird als CSV exportiert'
                }
              });
              if (res.data) {
                downloadURI(res.data.fileUrl, `chat-export-${id}.csv`);
              }
            }}>
            <Download size={20} className="" />
          </Button>
        )}
      </div>
      <div className="p-5 bg-white rounded-b-[14px] flex flex-col justify-between flex-grow overflow-hidden gap-2">
        <div className="flex flex-col gap-6 overflow-auto custom-scrollbar pr-3" ref={chat}>
          {isLoading && <Preloader />}
          {messages.map((message, index) => {
            const isOwn = messageIsOwn(message);
            let withRef: {
              ref?: any;
            } = {};

            if (message.files && !Array.isArray(message.files)) {
              message.files = [message.files];
            }

            if (messages.length === index + 1) {
              withRef = {
                ref: lastPostElementRef
              };
            }

            return isOwn ? (
              <OwnMessage
                message={message}
                key={message._id}
                type={type}
                id={id}
                setShowGoogleRating={setGoogleRatingModalOpen}
                setReload={setReload}
                {...withRef}
              />
            ) : (
              <OtherMessage
                message={message}
                key={message._id}
                type={type}
                id={id}
                setShowGoogleRating={setGoogleRatingModalOpen}
                setReload={setReload}
                {...withRef}
              />
            );
          })}
        </div>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-5">
            <div className="flex flex-col gap-2 rounded-lg px-3 py-2 border border-[#D0D5DD]">
              <div className="flex gap-2">
                {files && files.length > 0 && (
                  <>
                    {files.map((file, index) => (
                      <div className="border p-2 rounded-xl flex justify-start gap-2 self-start" key={index}>
                        <span className="font-medium">{file.name}</span>
                        <Button
                          variant="link"
                          onClick={(e) => {
                            setFiles((prev) => {
                              if (prev) {
                                return prev.filter((_, i) => i !== index);
                              }
                              return null;
                            });
                            fileRef.current?.value && (fileRef.current.value = '');
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          className="p-0 self-center m-0 h-fit">
                          <X size={20} />
                        </Button>
                      </div>
                    ))}
                  </>
                )}
              </div>
              <div className="flex justify-end items-center gap-4">
                <FormField
                  control={form.control}
                  name="message"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormControl>
                        <Textarea
                          {...field}
                          placeholder="Schreibe eine Nachricht"
                          className="w-full custom-scrollbar resize-none min-h-20"
                          disabled={submitting}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="flex flex-col gap-2">
                  <Button variant="default" type="submit" disabled={submitting}>
                    {submitting && <LoaderCircle className="animate-spin" />}
                    Senden
                  </Button>
                  <ChatFileUpload setFile={setFiles} fileRef={fileRef} />
                </div>
              </div>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
};

const ChatFileUpload = ({
  setFile,
  fileRef
}: {
  setFile: Dispatch<SetStateAction<File[] | null>>;
  fileRef: MutableRefObject<HTMLInputElement | null>;
}) => {
  const handleUpload = (files: File[]) => {
    setFile((prev) => {
      if (prev) {
        return [...prev, ...files];
      } else {
        return [...files];
      }
    });
  };

  const handleUploadClick = () => {
    const uploadBtn = document.querySelector(`#upload-chat`) as HTMLButtonElement | null;
    if (uploadBtn) {
      uploadBtn.click();
    }
  };

  return (
    <div className="self-end w-fit">
      <Paperclip onClick={handleUploadClick} className="cursor-pointer" />
      <Input
        type="file"
        multiple
        ref={fileRef}
        className="hidden"
        id="upload-chat"
        onChange={(e) => {
          e.preventDefault();
          if (e.target?.files && e.target?.files.length > 0) {
            handleUpload(Array.from(e.target.files));
          }
        }}
      />
    </div>
  );
};

const OwnMessage = forwardRef(
  (
    {
      message,
      type,
      id,
      setShowGoogleRating,
      setReload
    }: {
      message: ChatDto;
      type: 'internal' | 'customer';
      id: string;
      setShowGoogleRating: Dispatch<SetStateAction<boolean>>;
      setReload: Dispatch<SetStateAction<boolean>>;
    },
    ref: React.Ref<HTMLDivElement>
  ) => {
    const { apiRequest } = useApiRequest();
    const { user } = useUser();

    const canDeleteMessages = user?.userType === UserType.Internal && (user.userRole === UserRole.Admin || user.userRole === UserRole.SuperAdmin);

    const deleteMessage = async () => {
      await apiRequest(`jobs/internal/messages/${message._id}`, 'DELETE', {
        toast: {
          toastText: 'Nachricht wurde gelöscht'
        }
      });
      window.location.reload();
    };

    const downloadFile = async (file: JobFileResponseDto) => {
      const res = await apiRequest<{
        fileUrl: string;
      }>(`jobs/${type}/${id}/messages/${message._id}/${file.path}`, 'GET', {
        toast: {
          toastText: 'Datei wird heruntergeladen'
        }
      });
      if (res.data) {
        downloadURI(res.data.fileUrl, file.path);
      }
    };

    return (
      <div key={message._id} className="flex gap-3 w-full justify-end" ref={ref}>
        <div className={`flex flex-col gap-2 ${message.isRating ? 'w-full' : 'max-w-[calc(100%-60px)]'}`}>
          <div className="flex justify-between items-center gap-4">
            <span className="font-medium text-sm truncate">{message.isOwn ? 'Du' : message.belongsToUserFullName}</span>
            <span className="text-xs whitespace-nowrap">{formatDateTime(message.createdAt)}</span>
            {canDeleteMessages && (
              <div className="">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="h-8 w-8 p-0">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      className="text-red-600"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                      }}
                      asChild>
                      <ConfirmationDialog
                        onConfirm={deleteMessage}
                        title="Nachricht löschen"
                        description="Sind Sie sicher, dass Sie diese Nachricht löschen möchten?"
                        variant="destructive">
                        <Button variant="ghost" className="text-red-600">
                          <Trash2 className="mr-2 h-4 w-4" />
                          Löschen
                        </Button>
                      </ConfirmationDialog>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
          </div>
          {message.isRating ? (
            <FeedbackMessage
              message={message}
              canProvideFeedback={user?.userType === UserType.Customer}
              id={id}
              setShowGoogleRating={setShowGoogleRating}
              setReload={setReload}
            />
          ) : (
            <div className="flex flex-col gap-2 bg-primary rounded-b-lg rounded-tl-lg p-2">
              {message.files && message.files.length > 0 && <ChatFileDisplay files={message.files} downloadFile={downloadFile} variant="primary" />}
              {message.message && (
                <div className="bg-primary text-white rounded-lg self-end max-w-full">
                  <div className="py-2 px-3 text-sm">{parseText(message.message)}</div>
                </div>
              )}
            </div>
          )}
        </div>

        <img
          src={message.belongsToUserProfilePictureUrl ?? '/user-default.svg'}
          alt={message.isOwn ? 'Du' : message.belongsToUserFullName}
          width={40}
          height={40}
          className="rounded-full h-12 w-12 flex-shrink-0 object-cover"
        />
      </div>
    );
  }
);

const OtherMessage = forwardRef(
  (
    {
      message,
      type,
      id,
      setShowGoogleRating,
      setReload
    }: {
      message: ChatDto;
      type: 'internal' | 'customer';
      id: string;
      setShowGoogleRating: Dispatch<SetStateAction<boolean>>;
      setReload: Dispatch<SetStateAction<boolean>>;
    },
    ref: React.Ref<HTMLDivElement>
  ) => {
    const { apiRequest } = useApiRequest();
    const { user } = useUser();

    const canDeleteMessages = user?.userType === UserType.Internal && (user.userRole === UserRole.Admin || user.userRole === UserRole.SuperAdmin);

    const deleteMessage = async () => {
      await apiRequest(`jobs/internal/messages/${message._id}`, 'DELETE', {
        toast: {
          toastText: 'Nachricht wurde gelöscht'
        }
      });
      window.location.reload();
    };

    const downloadFile = async (file: JobFileResponseDto) => {
      const res = await apiRequest<{
        fileUrl: string;
      }>(`jobs/${type}/${id}/messages/${message._id}/${file.path}`, 'GET', {
        toast: {
          toastText: 'Datei wird heruntergeladen'
        }
      });
      if (res.data) {
        downloadURI(res.data.fileUrl, file.path);
      }
    };

    return (
      <div key={message._id} className="flex gap-3" ref={ref}>
        <img
          src={message.belongsToUserProfilePictureUrl ?? '/user-default.svg'}
          alt="Max Mitarbeiter"
          width={40}
          height={40}
          className="rounded-full h-12 w-12 object-cover"
        />
        <div className={`flex flex-col gap-2 items-start ${message.isRating ? 'w-full' : ''}`}>
          <div className="flex justify-between items-center gap-4">
            <span className="font-medium text-sm">{message.belongsToUserFullName}</span>
            <div className="flex items-center gap-2">
              <span className="text-xs">{formatDateTime(message.createdAt)}</span>
              {canDeleteMessages && (
                <div className="">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="h-8 w-8 p-0">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem className="text-red-600" asChild>
                        <ConfirmationDialog
                          onConfirm={deleteMessage}
                          title="Nachricht löschen"
                          description="Sind Sie sicher, dass Sie diese Nachricht löschen möchten?"
                          variant="destructive">
                          <Button variant="ghost" className="text-red-600">
                            <Trash2 className="mr-2 h-4 w-4" />
                            Löschen
                          </Button>
                        </ConfirmationDialog>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              )}
            </div>
          </div>
          {message.isRating ? (
            <FeedbackMessage
              message={message}
              canProvideFeedback={user?.userType === UserType.Customer}
              id={id}
              setShowGoogleRating={setShowGoogleRating}
              setReload={setReload}
            />
          ) : (
            <div className="flex flex-col gap-2">
              {message.files && message.files.length > 0 && <ChatFileDisplay files={message.files} downloadFile={downloadFile} />}
              {message.message && (
                <div className="border rounded-b-lg rounded-tr-lg">
                  <div className="py-2 px-3 text-sm">{parseText(message.message)}</div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }
);

const FeedbackMessage = ({
  message,
  canProvideFeedback,
  id,
  setShowGoogleRating,
  setReload
}: {
  message: ChatDto;
  canProvideFeedback: boolean;
  id: string;
  setShowGoogleRating: Dispatch<SetStateAction<boolean>>;
  setReload: Dispatch<SetStateAction<boolean>>;
}) => {
  const emojiMap = [
    { emoji: '🤬', value: 1 },
    { emoji: '😫', value: 2 },
    { emoji: '😐', value: 3 },
    { emoji: '😃', value: 4 },
    { emoji: '😍', value: 5 }
  ];

  const { apiRequest } = useApiRequest();

  const rateJob = async (rating: number) => {
    const res = await apiRequest<RatingInfoResponseDto>(`jobs/customer/${id}/messages/${message._id}/rating`, 'POST', {
      body: {
        rating
      },
      toast: {
        toastText: 'Vielen Dank für Dein Feedback'
      }
    });
    if (res.data && res.data.showGoogleRating) {
      setShowGoogleRating(true);
    } else {
      setReload((prev) => !prev);
    }
  };

  return (
    <div className="w-full rounded-[0px_8px_8px_8px] border-2 border-[#C0DE60] bg-[linear-gradient(96deg,_rgba(192,_222,_96,_0.07)_3.97%,_rgba(95,_213,_222,_0.07)_93.48%)] p-4">
      <div className="flex flex-col items-center justify-center gap-2">
        <p className="text-3xl font-bold">{message.rating ? 'Danke! 🎉' : 'Feedback zum Auftrag'}</p>
        <p className="text-[#888] text-base">
          {message.rating
            ? 'Wir haben Dein Feedback erhalten.'
            : canProvideFeedback
            ? 'Wie hat dir dieser Auftrag gefallen?'
            : 'Feedback wird gerade gesammelt'}
        </p>
        <div className="mt-2">
          <div className="w-fit rounded-full bg-primary">
            <div className="flex gap-4 px-3 py-2">
              {emojiMap.map((emoji, index) => (
                <button
                  key={index}
                  onClick={() => rateJob(emoji.value)}
                  disabled={!canProvideFeedback || message.rating !== null}
                  className={`flex h-5 w-5 items-center justify-center rounded-full text-2xl leading-none ${
                    message.rating === emoji.value ? 'disabled:opacity-100 shadow-[0_0_15px_5px_rgba(95,213,222,0.5)]' : 'disabled:opacity-50'
                  } transition-all duration-300 ${
                    !canProvideFeedback || message.rating !== null ? '' : 'hover:shadow-[0_0_15px_5px_rgba(95,213,222,0.5)]'
                  } `}>
                  {emoji.emoji}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const parseText = (description: string) => {
  const lines = description.split('\n');
  return lines.map((line, index) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;

    const updatedLine = line.replace(
      urlRegex,
      '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-blue-400 underline hover:no-underline break-all">$1</a>'
    );

    return (
      <p
        key={index}
        dangerouslySetInnerHTML={{
          __html: updatedLine.length > 0 ? updatedLine : '<br />'
        }}
        className="whitespace-pre-wrap break-word"
      />
    );
  });
};

const ChatFileDisplay = ({
  files,
  downloadFile,
  variant = 'default'
}: {
  files: JobFileResponseDto[];
  downloadFile: (file: JobFileResponseDto) => Promise<void>;
  variant?: 'primary' | 'default';
}) => {
  const isPrimary = variant === 'primary';
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [loadingImages, setLoadingImages] = useState<Record<number, boolean>>({});

  const handleImageClick = (index: number) => {
    setSelectedImageIndex(index);
    setImageDialogOpen(true);
  };

  const navigateImage = (direction: 'next' | 'prev') => {
    const imageFiles = files.filter((file) => file.thumbnailUrl);
    if (direction === 'next') {
      setSelectedImageIndex((prev) => (prev + 1) % imageFiles.length);
    } else {
      setSelectedImageIndex((prev) => (prev - 1 + imageFiles.length) % imageFiles.length);
    }
  };

  const imageFiles = files.filter((file) => file.thumbnailUrl);
  const nonImageFiles = files.filter((file) => !file.thumbnailUrl);
  const currentFile = imageFiles[selectedImageIndex];

  const handleImageLoad = (index: number) => {
    setLoadingImages((prev) => ({
      ...prev,
      [index]: false
    }));
  };

  const handleImageLoadStart = (index: number) => {
    setLoadingImages((prev) => ({
      ...prev,
      [index]: true
    }));
  };

  return (
    <>
      <div
        className={`flex justify-between items-start gap-3 px-3 py-2 flex-col ${
          isPrimary ? 'bg-primary text-white' : 'text-sm mr-2 border-[#D0D5DD] border rounded-b-lg rounded-tr-lg'
        }`}>
        <div className="flex gap-2 flex-wrap">
          {imageFiles.map((file, index) => (
            <div key={index} className="flex flex-col">
              <div className="cursor-pointer relative" onClick={() => handleImageClick(imageFiles.indexOf(file))}>
                {loadingImages[index] !== false && <Skeleton className="h-20 w-20 rounded absolute inset-0" />}
                <img
                  src={file.thumbnailUrl}
                  alt={file.name}
                  className="h-20 w-20 object-cover rounded"
                  onLoad={() => handleImageLoad(index)}
                  onLoadStart={() => handleImageLoadStart(index)}
                  onError={() => handleImageLoad(index)}
                  style={{ opacity: loadingImages[index] === false ? 1 : 0 }}
                />
              </div>
            </div>
          ))}
        </div>
        {nonImageFiles.map((file) => (
          <div className="flex gap-2 items-center" key={file.name}>
            <FileType />
            <div className="flex text-sm justify-between">
              <p className="font-medium self-center">{file.name}</p>
              <Button variant="link" className={isPrimary ? 'self-end' : 'text-[#12282A] self-end'} onClick={() => downloadFile(file)}>
                <Download size={20} className={isPrimary ? 'text-white' : ''} />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={imageDialogOpen} onOpenChange={setImageDialogOpen}>
        <DialogContent className="max-w-screen-lg w-full p-0 bg-black flex flex-col h-[90vh]">
          <DialogHeader className="flex-none">
            <DialogTitle />
          </DialogHeader>
          <div className="flex justify-between items-center p-4 text-white flex-none">
            <Button onClick={() => setImageDialogOpen(false)} variant="ghost" className="text-white hover:bg-gray-800">
              <X size={24} />
            </Button>
            <span className="font-medium">{currentFile?.name}</span>
            <Button variant="ghost" className="text-white hover:bg-gray-800" onClick={() => currentFile && downloadFile(currentFile)}>
              <Download size={24} />
            </Button>
          </div>

          <div className="flex-1 overflow-auto relative">
            <div className="min-h-full flex items-center justify-center relative">
              {currentFile?.thumbnailUrl && (
                <>
                  <div className={`absolute inset-0 flex items-center justify-center ${loadingImages[selectedImageIndex] === false ? 'hidden' : ''}`}>
                    <Skeleton className="w-full h-full max-w-screen-md max-h-[80vh] object-contain" />
                  </div>
                  <img
                    src={currentFile.thumbnailUrl}
                    alt={currentFile.name}
                    className="max-h-full max-w-full object-contain"
                    onLoad={() => handleImageLoad(selectedImageIndex)}
                    onLoadStart={() => handleImageLoadStart(selectedImageIndex)}
                    onError={() => handleImageLoad(selectedImageIndex)}
                    style={{ opacity: loadingImages[selectedImageIndex] === false ? 1 : 0 }}
                  />
                </>
              )}
            </div>
          </div>

          {imageFiles.length > 1 && (
            <>
              <Button
                className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full w-10 h-10 p-0 bg-black/50 hover:bg-black/70 z-10"
                onClick={() => navigateImage('prev')}>
                <ChevronLeft size={24} />
              </Button>
              <Button
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full w-10 h-10 p-0 bg-black/50 hover:bg-black/70 z-10"
                onClick={() => navigateImage('next')}>
                <ChevronRight size={24} />
              </Button>
            </>
          )}

          {imageFiles.length > 1 && (
            <div className="p-4 flex justify-center gap-2 flex-none">
              {imageFiles.map((file, index) => (
                <div
                  key={index}
                  className={`w-2 h-2 rounded-full ${index === selectedImageIndex ? 'bg-white' : 'bg-gray-500 cursor-pointer'}`}
                  onClick={() => setSelectedImageIndex(index)}
                />
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Chat;
